import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import CustomDialog from "../../components/modals/CustomDialog/CustomDialog";
import { Button } from "../../components/forms/Buttons/Button";
import { getQuestions, getStatuses } from "../../services/questionService";
import { addMultipleQuestionsToTest, createTest } from "../../services/Exams/ExamsService";
import { getApiErrorMessage } from "../../utils/apiError";
import { isCanceledError } from "../../utils/examError";
import { fetchAllPages } from "../../utils/fetchAllPages";
import { Spinner } from "../Exams/TestBuilder/ui";

const listOf = (data) => (Array.isArray(data) ? data : data?.results || []);
// The questions of one passage stay together, where its first one comes (a test keeps a passage in one block).
const passagesTogether = (questions) => {
  const groups = new Map();
  questions.forEach((question) => {
    const key = question.passage?.uuid || `q:${question.uuid}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(question);
  });
  return [...groups.values()].flat();
};

// The approved status's uuid (the questions API filters by it).
const approvedStatus = (signal) =>
  getStatuses({ params: { page: 1, page_size: 100 }, signal }).then((res) => listOf(res).find((s) => s.code === "APPROVED")?.uuid || null);

/**
 * A test from a subject folder at once: every approved question of the folder (oldest first, a passage's
 * questions together, each worth its difficulty's points), under a name the user may change; then the test opens
 * in the builder, to be put in order. `folder` is the subject, `examName` its exam folder's name.
 */
export default function TestFromFolderDialog({ isOpen, folder, examName, onClose }) {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  // The approved questions' count: undefined while read, null if it could not be.
  const [count, setCount] = useState(undefined);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !folder) return undefined;
    setName([examName, folder.name].filter(Boolean).join(" · "));
    setCount(undefined);
    setError("");
    setSaving(false);
    const controller = new AbortController();
    approvedStatus(controller.signal)
      .then((uuid) => {
        setStatus(uuid);
        if (!uuid) return null;
        return getQuestions({ params: { page: 1, page_size: 1, folder: folder.uuid, status: uuid }, signal: controller.signal }).then((res) =>
          setCount(typeof res?.count === "number" ? res.count : listOf(res).length)
        );
      })
      .catch((err) => {
        if (!isCanceledError(err)) setCount(null);
      });
    return () => controller.abort();
  }, [isOpen, folder, examName]);

  const create = async (event) => {
    event.preventDefault();
    if (saving || !name.trim() || !count) return;
    setSaving(true);
    setError("");
    try {
      const questions = passagesTogether(
        await fetchAllPages((page) => getQuestions({ params: { ...page, folder: folder.uuid, status, ordering: "created" } }), 100)
      );
      const test = await createTest({
        data: { name: name.trim(), description: "", heading: "", calculator_is_allowed: false, periodic_table_is_allowed: false, additional_information: "" },
      });
      await addMultipleQuestionsToTest({
        params: { uuid: test.uuid },
        data: { questions: questions.map((question) => ({ test: test.uuid, question_id: question.uuid, points: question.points ?? 1 })) },
      });
      navigate(`/test/${test.uuid}`, { state: { justCreated: true, from: "/test_list" } });
    } catch (err) {
      setError(getApiErrorMessage(t, err, "folders_page.test.failed"));
      setSaving(false);
    }
  };

  return (
    <CustomDialog isOpen={isOpen} closeModal={() => !saving && onClose()} headerText={t("folders_page.test.title")} maxWidth="max-w-lg" zIndex="z-40">
      <form onSubmit={create} className="space-y-4">
        <p className="text-sm text-slate-600">{t("folders_page.test.intro", { name: folder?.name || "" })}</p>
        <div>
          <label htmlFor="folder-test-name" className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("folders_page.test.name")}
          </label>
          <input
            id="folder-test-name"
            type="text"
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
            className="h-10 w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 max-sm:h-11"
          />
        </div>
        <p className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700" role="status">
          {count === undefined ? (
            <>
              <Spinner className="h-4 w-4" />
              {t("folders_page.test.counting")}
            </>
          ) : count === null ? (
            t("folders_page.test.count_failed")
          ) : count ? (
            t("folders_page.test.count", { count })
          ) : (
            t("folders_page.test.none")
          )}
        </p>
        {error && (
          <p role="alert" className="flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-700">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={onClose} disabled={saving} className="max-sm:h-11" />
          <Button type="submit" color="primary" text={t("folders_page.test.create")} loading={saving} disabled={!count || !name.trim()} className="max-sm:h-11" />
        </div>
      </form>
    </CustomDialog>
  );
}
